import { useCallback, useEffect, useState } from "react";

export const TURNSTILE_ERROR_MESSAGE =
  "Your device failed an integrity check. Please refresh the page to try again.";

declare global {
  interface Window {
    turnstile: Turnstile.Turnstile;
  }
}

/**
 * @param turnstileSitekey - Public identifier for the turnstile widget.
 * @returns An object with the following properties:
 * - turnstileToken: The token granted by turnstile on successful completion.
 * - error: A user-friendly error message if the widget fails, or fails to load.
 * - reset: A function to reset the turnstile widget and get a new token.
 *
 * The widget starts the integrity check process as soon as it is rendered.
 *
 * When it completes, the token is returned.
 *
 * Tokens are single-use. If you pass the token to the backend, you will need
 * a new token for the next request. Call reset to restart the process and get
 * a new token. The token will be null once again until the process completes.
 */
export function useTurnstile(
  turnstileSitekey: string | undefined,
  placeholderId: string,
) {
  const [isTurnstileLoaded, setIsTurnstileLoaded] = useState(false);
  const [isTurnstileRendered, setIsTurnstileRendered] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);

  useEffect(() => {
    // If we don't have a sitekey, turnstile must be disabled
    if (!turnstileSitekey) {
      setIsTurnstileLoaded(false);
      return;
    }

    // If turnstile is already loaded, don't poll for it
    if (window.turnstile) {
      setIsTurnstileLoaded(true);
      return;
    }

    // Poll until turnstile is loaded.
    const pollForTurnstileInterval = setInterval(() => {
      if (window.turnstile) {
        setIsTurnstileLoaded(true);
        clearInterval(pollForTurnstileInterval);
      }
    }, 250);

    const tookTooLongTimeout = setTimeout(() => {
      setError(TURNSTILE_ERROR_MESSAGE);
      clearInterval(pollForTurnstileInterval);
    }, 10000);

    return () => {
      clearInterval(pollForTurnstileInterval);
      clearTimeout(tookTooLongTimeout);
    };
  }, [turnstileSitekey]);

  useEffect(() => {
    if (!isTurnstileLoaded) {
      return;
    }

    try {
      if (isTurnstileRendered) {
        return;
      }

      setIsTurnstileRendered(true);
      const renderedWidgetId = window.turnstile.render(`#${placeholderId}`, {
        sitekey: turnstileSitekey!,
        theme: "light",
        callback: (token: string) => {
          setTurnstileToken(token);
        },
        "error-callback": (e: any) => {
          setError(TURNSTILE_ERROR_MESSAGE);
          OsPlatform.captureError(e, { tags: { turnstile: true } });
        },
      });

      setWidgetId(renderedWidgetId ?? null);
    } catch (e) {
      setError(TURNSTILE_ERROR_MESSAGE);
      OsPlatform.captureError(e, { tags: { turnstile: true } });
    }
  }, [
    isTurnstileLoaded,
    isTurnstileRendered,
    turnstileSitekey,
    setWidgetId,
    placeholderId,
  ]);

  const reset = useCallback(() => {
    setTurnstileToken(null);
    setError(null);
    if (widgetId) {
      window.turnstile.reset(widgetId);
    }
  }, [widgetId]);

  return { turnstileToken, error, reset };
}
